import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  BOSSES, BOSS_KINDS, TOWER_BOSSES, RAID_BOSSES, REWARD_TAGS,
  rewardTags, bossFaq, bossPreset, type BossKind, type RewardTag,
} from "@/lib/bosses-data";
import { BossArt } from "@/components/BossArt";
import { ShareButton } from "@/components/ShareButton";
import { FaqSection, faqSchema, RelatedGuides, type FaqItem } from "@/components/PageFaq";

import { snippetFor } from "@/lib/seo-snippets";
import { activeVariant } from "@/lib/seo-ab-titles";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";

const URL = "https://palstars.com/bosses";
const SNIPPET = snippetFor("/bosses")!;
const AB = activeVariant("/bosses");

/** Per-boss Q&A rendered inside each boss card; also emitted as FAQPage JSON-LD. */
const BOSS_FAQ: FaqItem[] = BOSSES.flatMap((b) => bossFaq(b));


const FAQ: FaqItem[] = [
  {
    q: "How many bosses are in Palworld?",
    a: `Palworld 1.0 has ${TOWER_BOSSES.length} story Tower Bosses, ${RAID_BOSSES.length} altar-summoned raid bosses, plus Predator Pals and field Alphas — ${BOSSES.length} major encounters are catalogued here with levels, weaknesses and rewards.`,
  },
  {
    q: "What order should you fight Palworld bosses in?",
    a: TOWER_BOSSES.map((b) => `${b.name} (Lv ${b.level})`).join(" → ") + ", then raid bosses once the Summoning Altar is unlocked.",
  },
  {
    q: "What is the hardest boss in Palworld?",
    a: "Xenolord has the biggest health pool of any raid boss at roughly 500,000 HP, and Saya & Selyne is the hardest Tower Boss at level 60 — both expect fully condensed Pals and Legendary weapons.",
  },
  {
    q: "Can you capture Palworld bosses?",
    a: "Tower Boss Pals cannot be captured, but raid bosses, Predator Pals and field Alphas can all be caught with Legendary Spheres once they are below about 20% health.",
  },
  {
    q: "Do Palworld bosses respawn?",
    a: "Yes. Tower Bosses can be re-challenged immediately, raid bosses can be re-summoned with another Slab, and field and Predator spawns return roughly every in-game day or after a server restart.",
  },
];

export const Route = createFileRoute("/bosses")({
  head: () => ({
    // CTR override: this page collects the most impressions on the site (214)
    // for "alpha boss map" / "alpha pals by level" style queries, so the
    // snippet names the map, the levels and the weakness list up front.
    meta: [
      // Title/description are under a weekly A/B test (see seo-ab-titles.ts).
      { title: AB?.title ?? SNIPPET.title },
      { name: "description", content: AB?.description ?? SNIPPET.description },
      { property: "og:title", content: SNIPPET.social?.title ?? SNIPPET.title },
      { property: "og:description", content: SNIPPET.social?.description ?? SNIPPET.description },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: SNIPPET.social?.title ?? SNIPPET.title },
      { name: "twitter:description", content: SNIPPET.social?.description ?? SNIPPET.description },
      { name: "robots", content: "index, follow, max-snippet:-1, max-image-preview:large" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      faqSchema([...FAQ, ...BOSS_FAQ], URL),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Palworld bosses",
          numberOfItems: BOSSES.length,
          itemListElement: BOSSES.map((b, i) => ({ "@type": "ListItem", position: i + 1, name: b.name })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: { "@id": "https://palstars.com/", name: "Home" } },
            { "@type": "ListItem", position: 2, name: "Bosses", item: { "@id": URL, name: "Bosses" } },
          ],
        }),
      },
    ],
  }),
  component: BossesPage,
});

const ELEMENTS = ["All", ...Array.from(new Set(BOSSES.map((b) => b.element)))];

function BossesPage() {
  const [kind, setKind] = useState<BossKind | "All">("All");
  const [element, setElement] = useState("All");
  const [reward, setReward] = useState<RewardTag | "All">("All");

  const shown = useMemo(
    () =>
      BOSSES.filter(
        (b) =>
          (kind === "All" || b.kind === kind) &&
          (element === "All" || b.element === element) &&
          (reward === "All" || rewardTags(b).includes(reward)),
      ).sort((a, b) => a.level - b.level),
    [kind, element, reward],
  );


  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      {/* Visible breadcrumb trail comes from the site layout (src/components/Breadcrumbs.tsx). */}
      <h1 className="mt-3 text-3xl sm:text-4xl font-bold">Palworld Bosses</h1>
      <p className="aeo-answer text-muted-foreground mt-2 max-w-3xl">
        Palworld has {TOWER_BOSSES.length} story Tower Bosses, {RAID_BOSSES.length} altar-summoned raid bosses and
        roaming Predator and Alpha bosses. Every entry below lists its level, health pool, element weakness, best
        counter Pals, location and rewards, and links to a full boss guide with an FAQ.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <ShareButton title="Palworld Bosses — levels, weaknesses & rewards" text="Every Palworld boss with weaknesses, counters and rewards." path="/bosses" />
        <Link to="/guides/$slug" params={{ slug: "palworld-all-bosses-guide" }} className="text-sm text-primary hover:underline">
          Read the full bosses guide →
        </Link>
        <Link to="/damage" search={{ boss: undefined }} className="text-sm text-primary hover:underline">
          Check your DPS in the damage calculator →
        </Link>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {(["All", ...BOSS_KINDS] as (BossKind | "All")[]).map((k) => (
          <button key={k} onClick={() => setKind(k)}
            className={`rounded-full px-3 py-1.5 text-xs font-semibold ${kind === k ? "bg-primary text-primary-foreground" : "glass hover:brightness-110"}`}>
            {k === "All" ? "All bosses" : `${k}es`}
          </button>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {ELEMENTS.map((e) => (
          <button key={e} onClick={() => setElement(e)}
            className={`rounded-full px-3 py-1.5 text-xs ${element === e ? "bg-primary text-primary-foreground" : "glass hover:brightness-110"}`}>
            {e === "All" ? "All elements" : e}
          </button>
        ))}
      </div>

      <div className="mt-2 flex flex-wrap gap-2">
        {(["All", ...REWARD_TAGS] as (RewardTag | "All")[]).map((r) => (
          <button key={r} onClick={() => setReward(r)}
            aria-pressed={reward === r}
            className={`rounded-full px-3 py-1.5 text-xs ${reward === r ? "bg-primary text-primary-foreground" : "glass hover:brightness-110"}`}>
            {r === "All" ? "All rewards" : r}
          </button>
        ))}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Showing {shown.length} of {BOSSES.length} bosses. Boss artwork shows the official Pal for each encounter and is
        used for identification purposes only — Palworld and all Pal designs are © Pocketpair, Inc.
      </p>


      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mt-6">
        {shown.map((b) => {
          const preset = bossPreset(b);
          return (
          <article key={b.slug} className="glass rounded-2xl p-5">
            <div className="mx-auto max-w-[150px]">
              <BossArt boss={b} size={150} />
            </div>
            <h2 className="mt-3 text-lg font-semibold">{b.name}</h2>
            <div className="mt-1 flex flex-wrap gap-1.5 text-[10px] uppercase tracking-widest">
              <span className="rounded-full px-2 py-0.5" style={{ background: b.accent + "33", color: b.accent }}>{b.element}</span>
              <span className="rounded-full glass px-2 py-0.5">{b.kind}</span>
              <span className="rounded-full glass px-2 py-0.5">Lv {b.level}</span>
              {rewardTags(b).map((t) => (
                <span key={t} className="rounded-full glass px-2 py-0.5">{t}</span>
              ))}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{b.desc}</p>
            <dl className="mt-3 text-xs space-y-1">
              <div className="flex gap-2"><dt className="text-muted-foreground w-24">Health</dt><dd className="font-medium">{b.hp}</dd></div>
              <div className="flex gap-2"><dt className="text-muted-foreground w-24">Weak to</dt><dd className="font-medium">{b.weakness}</dd></div>
              <div className="flex gap-2"><dt className="text-muted-foreground w-24">Resists</dt><dd className="font-medium">{b.resists}</dd></div>
              <div className="flex gap-2"><dt className="text-muted-foreground w-24">Location</dt><dd className="font-medium">{b.location}</dd></div>
              <div className="flex gap-2"><dt className="text-muted-foreground w-24">Timer</dt><dd className="font-medium">{b.timeLimit}</dd></div>
              <div className="flex gap-2"><dt className="text-muted-foreground w-24">Counters</dt><dd className="font-medium">{b.recommendedPals.join(", ")}</dd></div>
              <div className="flex gap-2"><dt className="text-muted-foreground w-24">Rewards</dt><dd className="font-medium">{b.rewards.join(", ")}</dd></div>
            </dl>

            <details className="mt-3 rounded-xl border border-border/50 p-3">
              <summary className="cursor-pointer text-xs font-semibold">Build preset</summary>
              <dl className="mt-2 text-xs space-y-1">
                <div className="flex gap-2"><dt className="text-muted-foreground w-24">Weapon</dt><dd className="font-medium">{preset.weapon}</dd></div>
                <div className="flex gap-2"><dt className="text-muted-foreground w-24">Ammo</dt><dd className="font-medium">{preset.ammo}</dd></div>
                <div className="flex gap-2"><dt className="text-muted-foreground w-24">Armour</dt><dd className="font-medium">{preset.armour}</dd></div>
                <div className="flex gap-2"><dt className="text-muted-foreground w-24">Accessories</dt><dd className="font-medium">{preset.accessories}</dd></div>
                <div className="flex gap-2"><dt className="text-muted-foreground w-24">Consumables</dt><dd className="font-medium">{preset.consumables}</dd></div>
                <div className="flex gap-2"><dt className="text-muted-foreground w-24">Party</dt><dd className="font-medium">{preset.party.join(", ")}</dd></div>
                <div className="flex gap-2"><dt className="text-muted-foreground w-24">Spheres</dt><dd className="font-medium">{preset.sphere}</dd></div>
              </dl>
            </details>

            <details className="mt-2 rounded-xl border border-border/50 p-3">
              <summary className="cursor-pointer text-xs font-semibold">{b.name} FAQ</summary>
              <dl className="mt-2 space-y-2">
                {bossFaq(b).map((f) => (
                  <div key={f.q}>
                    <dt className="text-xs font-semibold">{f.q}</dt>
                    <dd className="aeo-answer text-xs text-muted-foreground">{f.a}</dd>
                  </div>
                ))}
              </dl>
            </details>

            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <Link to="/guides/$slug" params={{ slug: `palworld-${b.slug}-boss-guide` }} className="text-primary hover:underline">
                {b.name} guide &amp; FAQ →
              </Link>
              <Link to="/damage" search={{ boss: b.slug }} className="text-primary hover:underline">
                Calculate DPS vs {b.name} →
              </Link>
            </div>
          </article>
          );
        })}
      </div>



      <InternalLinkBlock path="/bosses" />
      <FaqSection items={FAQ} heading="Palworld bosses FAQ" />

      <RelatedGuides
        links={[
          { slug: "palworld-all-bosses-guide", label: "All Palworld Bosses Guide & FAQ" },
          { slug: "palworld-breeding-tier-list-guide", label: "Palworld Breeding Tier List Guide & FAQ" },
          { slug: "palworld-all-eggs-guide", label: "All Palworld Eggs Guide & FAQ" },
          { slug: "tier-list", label: "Palworld meta tier list" },
        ]}
      />
    </div>
  );
}
